/**
 * Production's **What Changed** narrative, copied verbatim from `report.tsx`
 * at `11ca09a`.
 *
 * **Extracted, not transcribed.** The two function bodies below were lifted out
 * of production character for character; only the module framing and the
 * quote style are ours. Regenerate rather than hand-edit.
 *
 * The rule: a five-point move on any of the four scores is significant, and a
 * change of primary type always is. Nothing significant produces the
 * "structural rather than situational" paragraph, which is a real outcome and
 * not an empty state.
 */

export function joinList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0] as string;
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

export function generateWhatChangedNarrative(
  current: {
    bottleneckScore: number;
    executionReadinessScore: number;
    recoveryDeficitScore: number;
    clarityScore: number;
    primaryType: string;
  },
  previous: {
    bottleneckScore: number;
    executionReadinessScore: number;
    recoveryDeficitScore: number;
    clarityScore: number;
    primaryType: string;
    completedAt: string;
  },
): string {
  const date = new Date(previous.completedAt).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const typeShifted = previous.primaryType !== current.primaryType;

  const SIG = 5;
  const bottleneckDelta = previous.bottleneckScore - current.bottleneckScore;
  const recoveryDelta = previous.recoveryDeficitScore - current.recoveryDeficitScore;
  const executionDelta = current.executionReadinessScore - previous.executionReadinessScore;
  const clarityDelta = current.clarityScore - previous.clarityScore;

  const bottleneckMoved = Math.abs(bottleneckDelta) >= SIG;
  const recoveryMoved = Math.abs(recoveryDelta) >= SIG;
  const executionMoved = Math.abs(executionDelta) >= SIG;
  const clarityMoved = Math.abs(clarityDelta) >= SIG;
  const anySignificant = typeShifted || bottleneckMoved || recoveryMoved || executionMoved || clarityMoved;

  if (!anySignificant) {
    return `Your scores have not shifted meaningfully since ${date}. That is important data. When patterns do not move, it indicates they are structural rather than situational. The bottleneck you identified is still your primary constraint. Your next reset cycle should focus with more precision on a single behavior change rather than spreading effort across multiple areas at once.`;
  }

  const improvementDescriptions: string[] = [];
  const regressionDescriptions: string[] = [];

  if (bottleneckMoved) {
    const pts = Math.abs(Math.round(bottleneckDelta));
    if (bottleneckDelta > 0) {
      improvementDescriptions.push(`your Bottleneck Score dropped ${pts} points, indicating the friction blocking your primary output has decreased`);
    } else {
      regressionDescriptions.push(`your Bottleneck Score rose ${pts} points, signaling that your primary performance constraint has intensified`);
    }
  }

  if (recoveryMoved) {
    const pts = Math.abs(Math.round(recoveryDelta));
    if (recoveryDelta > 0) {
      improvementDescriptions.push(`your Recovery Deficit dropped ${pts} points, meaning your system is carrying more reserve than it was`);
    } else {
      regressionDescriptions.push(`your Recovery Deficit increased ${pts} points, indicating you are running on less reserve than your last assessment showed`);
    }
  }

  if (executionMoved) {
    const pts = Math.abs(Math.round(executionDelta));
    if (executionDelta > 0) {
      improvementDescriptions.push(`your Execution Readiness improved ${pts} points, reflecting a stronger capacity for focused, high-quality work`);
    } else {
      regressionDescriptions.push(`your Execution Readiness declined ${pts} points, suggesting more structural interference is sitting between your intentions and your output`);
    }
  }

  if (clarityMoved) {
    const pts = Math.abs(Math.round(clarityDelta));
    if (clarityDelta > 0) {
      improvementDescriptions.push(`your Clarity Score increased ${pts} points, showing more directional certainty in how you are allocating attention and effort`);
    } else {
      regressionDescriptions.push(`your Clarity Score dropped ${pts} points, indicating that uncertainty, competing priorities, or identity pressure is creating more drag on your decision-making`);
    }
  }

  const hasImprovements = improvementDescriptions.length > 0;
  const hasRegressions = regressionDescriptions.length > 0;
  const isMixed = hasImprovements && hasRegressions;
  const isAllImproved = hasImprovements && !hasRegressions;
  const isAllRegressed = !hasImprovements && hasRegressions;

  const parts: string[] = [];

  if (typeShifted) {
    parts.push(
      `Your primary type has shifted from ${previous.primaryType} to ${current.primaryType} since ${date}. This is a meaningful signal. It does not mean the previous pattern has resolved entirely. It means the balance of friction in your performance system has changed enough that a different constraint is now generating the most drag.`,
    );
  }

  if (isAllImproved) {
    const list = joinList(improvementDescriptions);
    if (typeShifted) {
      parts.push(
        `The score data supports this shift: ${list}. The reset work from your last cycle produced measurable movement. That does not mean the work is finished. It means you have cleared enough friction in one area that the system has rebalanced around a different constraint. Your next 30 days should focus on embedding the behaviors that drove this progress so they hold when pressure increases again.`,
      );
    } else {
      parts.push(
        `Since ${date}, ${list}. The direction is right. The question now is whether these gains represent a structural change in how you operate or a temporary reduction in external load. Use the next 30 days to build the behaviors that drove this improvement into your baseline so they survive when conditions get harder.`,
      );
    }
  } else if (isAllRegressed) {
    const list = joinList(regressionDescriptions);
    if (typeShifted) {
      parts.push(
        `The score data reflects the cost of this shift: ${list}. This is not a failure of effort. It is a signal about load. When multiple dimensions move in the wrong direction simultaneously, the most common cause is a systemic increase in demand without a corresponding reduction in commitments or increase in recovery.`,
      );
    } else {
      parts.push(
        `Since ${date}, ${list}. Regression across multiple dimensions at the same time points to one of three patterns: a significant increase in external pressure, a structural failure in recovery, or a clarity and identity issue that has gone unaddressed. Review the score breakdown on this page and identify which single dimension regressed most. Start there.`,
      );
    }
  } else if (isMixed) {
    const improvList = joinList(improvementDescriptions);
    const regressList = joinList(regressionDescriptions);
    if (typeShifted) {
      parts.push(
        `The score movement reflects why this shift happened: ${improvList}. At the same time, ${regressList}. This is one of the most common patterns on a second assessment. Addressing one bottleneck reduces friction in that area, which shifts the system's balance and surfaces the next constraint. The improvement is real. So is the new problem. Your focus needs to move to the dimension that regressed most.`,
      );
    } else {
      parts.push(
        `Since ${date}, the picture is mixed. On the improvement side, ${improvList}. On the regression side, ${regressList}. Mixed results like this usually mean one part of your system is genuinely improving while another is under new or unaddressed pressure. Treat the area with the most regression as your primary focus in the next reset cycle.`,
      );
    }
  } else if (typeShifted) {
    parts.push(
      `The score changes are each below the significance threshold individually, but the combination produced a type shift. Review the category breakdown carefully. The shift likely reflects a small but consistent movement across several categories rather than one dramatic change in a single area.`,
    );
  }

  return parts.join(' ');
}
